import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { Info } from "lucide-react";
import {
  currentPinningSessionAtom,
  designProjectPlacementIdsAtom,
  getPinningSessionForPlacement,
  pinningModeAtom,
  pinningSessionsAtom,
  type DesignLabPlacementKey,
  useCancelPinningSession
} from "@design-lab/entities/pinning";
import {
  trackDesignLabPinningInstructionsRemoved,
  trackDesignLabPinningModeEntered,
  trackDesignLabPinningSessionSummaryEdited
} from "@design-lab/shared/lib/analytics";
import { Button } from "@design-lab/shared/ui";
import { MarkdownContent } from "@design-lab/shared/ui/MarkdownContent";
import { Tooltip, TooltipTrigger } from "@design-lab/shared/ui/design-system/Tooltip";
import { useArtworkForm } from "@design-lab/entities/artwork";

type CompletedSessionIndicatorProps = {
  placementKey: DesignLabPlacementKey;
  placementSide: "front" | "back";
};

export function CompletedSessionIndicator({
  placementKey,
  placementSide
}: CompletedSessionIndicatorProps) {
  const { form } = useArtworkForm();
  const setPinningMode = useSetAtom(pinningModeAtom);
  const [pinningSessions, setPinningSessions] = useAtom(pinningSessionsAtom);
  const designProjectPlacementIds = useAtomValue(designProjectPlacementIdsAtom);
  const setCurrentPinningSession = useSetAtom(currentPinningSessionAtom);
  const { mutate: cancelSession, isPending: isCancellingSession } = useCancelPinningSession();

  const currentArtworkId = form.watch(`${placementKey}.artworkId`) as string | undefined;
  const { designProjectPlacementId, session: sessionForPlacement } = getPinningSessionForPlacement({
    placementKey,
    designProjectPlacementIds,
    pinningSessions
  });

  if (
    !designProjectPlacementId ||
    !sessionForPlacement ||
    sessionForPlacement.state !== "completed" ||
    !currentArtworkId
  ) {
    return null;
  }

  const { sessionId, summary } = sessionForPlacement;

  const handleCancel = () => {
    cancelSession(
      { sessionId, artworkId: currentArtworkId },
      {
        onSuccess: () => {
          trackDesignLabPinningInstructionsRemoved({
            artworkId: currentArtworkId,
            sessionId
          });
          setPinningSessions((prev: typeof pinningSessions) => {
            const next = { ...prev };
            delete next[designProjectPlacementId];
            return next;
          });
          form.setValue(`info.${placementSide}.enhancementRequestNote` as any, "");
        }
      }
    );
  };

  const handleEdit = () => {
    setCurrentPinningSession({
      designProjectPlacementId,
      artworkId: currentArtworkId,
      sessionId
    });

    if (summary) {
      trackDesignLabPinningSessionSummaryEdited({
        artworkId: currentArtworkId,
        sessionId
      });
    } else {
      trackDesignLabPinningModeEntered({
        artworkId: currentArtworkId,
        sessionId,
        hasExistingPins: true
      });
    }
    setPinningMode(true);
  };

  return (
    <div
      data-testid="completed-session-indicator"
      className="mb-4 flex flex-col gap-3 rounded-lg border-2 border-green-400 bg-green-100 p-4 pt-2"
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900 sm:text-base">
            Your Artist Edits
          </span>
          <TooltipTrigger delay={300}>
            <button
              type="button"
              data-testid="design-services-info-tooltip"
              className="inline-flex cursor-pointer items-center justify-center border-0 bg-transparent p-0"
              aria-label="Design services information"
            >
              <Info className="h-4 w-4 text-gray-500 hover:text-gray-700" />
            </button>
            <Tooltip
              className="w-[315px] bg-blue-700 p-3 text-sm lg:w-fit lg:max-w-[430px]"
              placement="top"
              offset={8}
              arrowClassName="fill-blue-700"
            >
              <p className="text-wrap">
                Our artists will perform the requested changes once the order is completed
              </p>
            </Tooltip>
          </TooltipTrigger>
        </div>
        {summary && (
          <div className="text-sm text-gray-700">
            <MarkdownContent content={summary} />
          </div>
        )}
      </div>
      <div className="flex gap-2 self-end">
        <Button
          type="button"
          data-testid="cancel-design-request-button"
          className="shrink-0 cursor-pointer rounded-lg border-2 border-red-400 bg-transparent px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
          isDisabled={isCancellingSession}
          onPress={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type="button"
          data-testid="edit-design-request-button"
          className="shrink-0 cursor-pointer rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800"
          onPress={handleEdit}
        >
          Edit
        </Button>
      </div>
    </div>
  );
}
